'use client';

import {
  CircularProgress,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useGetProductResultsDetailed } from '~/entities/SponsoredProducts/hooks';
import {
  StyledContainer,
  StyledHeaderBoxTableCell,
  StyledHeaderTableCell,
  StyledTable,
  StyledTableContainer,
} from '~/shared';
import SortIcon from '~/shared/icons/SortIcon';
import type { SponsoredProductsResultsDetailedModelView } from '~/types';

import { useProductSort } from '../hooks';

import { ProductRow } from './Row';

export function ProductsList({
  getProductResultsDetailedQuery,
}: {
  getProductResultsDetailedQuery: ReturnType<
    typeof useGetProductResultsDetailed
  >;
}) {
  const {
    sortedProducts,
    isSorting,
    sortByOrderName,
    sortByOrderImpressions,
    sortByOrderClicks,
    sortByOrderCTR,
    sortByOrderCPM,
    sortByOrderCPC,
    setSortByOrderName,
    setSortByOrderImpressions,
    setSortByOrderClicks,
    setSortByOrderCTR,
    setSortByOrderCPM,
    setSortByOrderCPC,
    toggleSort,
  } = useProductSort(getProductResultsDetailedQuery.data);

  const showSortIcons = sortedProducts.length > 1;

  return (
    <StyledContainer
      sx={theme => ({
        width: '100%',
        gridColumn: 'span 3',
        borderRadius: '8px',
        paddingTop: theme.spacing(3),
        [theme.breakpoints.down('lg')]: { gridColumn: 'span 2' },
        [theme.breakpoints.down('md')]: { gridColumn: 'span 1' },
      })}
    >
      <StyledTableContainer>
        <StyledTable aria-label="Tabela de Produtos">
          {!isSorting &&
            !getProductResultsDetailedQuery.isLoading &&
            !getProductResultsDetailedQuery.isFetching && (
              <TableHead>
                <TableRow>
                  <StyledHeaderTableCell sx={{ width: '0' }} align="center" />
                  <StyledHeaderTableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      backgroundColor: 'background.paper',
                    }}
                  >
                    {showSortIcons ? (
                      <StyledHeaderBoxTableCell
                        sx={{ justifyContent: 'flex-start', gap: 0.5 }}
                      >
                        <Typography
                          variant="body2"
                          component="p"
                          fontWeight={500}
                        >
                          Nome do Produto
                        </Typography>
                        <SortIcon
                          sortOrder={sortByOrderName}
                          onClick={() =>
                            toggleSort(sortByOrderName, setSortByOrderName)
                          }
                        />
                      </StyledHeaderBoxTableCell>
                    ) : (
                      <Typography
                        variant="body2"
                        component="p"
                        fontWeight={500}
                      >
                        Nome do Produto
                      </Typography>
                    )}
                  </StyledHeaderTableCell>
                  <StyledHeaderTableCell>
                    <Typography variant="body2" component="p" fontWeight={500}>
                      SKU
                    </Typography>
                  </StyledHeaderTableCell>

                  <StyledHeaderTableCell>
                    {showSortIcons ? (
                      <StyledHeaderBoxTableCell sx={{ gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          component="p"
                          fontWeight={500}
                        >
                          Impressões
                        </Typography>
                        <SortIcon
                          sortOrder={sortByOrderImpressions}
                          onClick={() =>
                            toggleSort(
                              sortByOrderImpressions,
                              setSortByOrderImpressions
                            )
                          }
                        />
                      </StyledHeaderBoxTableCell>
                    ) : (
                      <Typography
                        variant="body2"
                        component="p"
                        fontWeight={500}
                      >
                        Impressões
                      </Typography>
                    )}
                  </StyledHeaderTableCell>
                  <StyledHeaderTableCell>
                    {showSortIcons ? (
                      <StyledHeaderBoxTableCell sx={{ gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          component="p"
                          fontWeight={500}
                        >
                          Cliques
                        </Typography>
                        <SortIcon
                          sortOrder={sortByOrderClicks}
                          onClick={() =>
                            toggleSort(sortByOrderClicks, setSortByOrderClicks)
                          }
                        />
                      </StyledHeaderBoxTableCell>
                    ) : (
                      <Typography
                        variant="body2"
                        component="p"
                        fontWeight={500}
                      >
                        Cliques
                      </Typography>
                    )}
                  </StyledHeaderTableCell>
                  <StyledHeaderTableCell>
                    {showSortIcons ? (
                      <StyledHeaderBoxTableCell sx={{ gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          component="p"
                          fontWeight={500}
                        >
                          CTR
                        </Typography>
                        <SortIcon
                          sortOrder={sortByOrderCTR}
                          onClick={() =>
                            toggleSort(sortByOrderCTR, setSortByOrderCTR)
                          }
                        />
                      </StyledHeaderBoxTableCell>
                    ) : (
                      <Typography
                        variant="body2"
                        component="p"
                        fontWeight={500}
                      >
                        CTR
                      </Typography>
                    )}
                  </StyledHeaderTableCell>
                  <StyledHeaderTableCell>
                    {showSortIcons ? (
                      <StyledHeaderBoxTableCell sx={{ gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          component="p"
                          fontWeight={500}
                        >
                          CPM
                        </Typography>
                        <SortIcon
                          sortOrder={sortByOrderCPM}
                          onClick={() =>
                            toggleSort(sortByOrderCPM, setSortByOrderCPM)
                          }
                        />
                      </StyledHeaderBoxTableCell>
                    ) : (
                      <Typography
                        variant="body2"
                        component="p"
                        fontWeight={500}
                      >
                        CPM
                      </Typography>
                    )}
                  </StyledHeaderTableCell>
                  <StyledHeaderTableCell>
                    {showSortIcons ? (
                      <StyledHeaderBoxTableCell sx={{ gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          component="p"
                          fontWeight={500}
                        >
                          CPC
                        </Typography>
                        <SortIcon
                          sortOrder={sortByOrderCPC}
                          onClick={() =>
                            toggleSort(sortByOrderCPC, setSortByOrderCPC)
                          }
                        />
                      </StyledHeaderBoxTableCell>
                    ) : (
                      <Typography
                        variant="body2"
                        component="p"
                        fontWeight={500}
                      >
                        CPC
                      </Typography>
                    )}
                  </StyledHeaderTableCell>
                </TableRow>
              </TableHead>
            )}

          <TableBody>
            {isSorting && (
              <TableRow>
                <TableCell
                  component="th"
                  scope="row"
                  colSpan={10}
                  align="center"
                >
                  <CircularProgress size={15} /> Carregando...
                </TableCell>
              </TableRow>
            )}

            {!isSorting &&
              (getProductResultsDetailedQuery.isLoading ||
                getProductResultsDetailedQuery.isFetching) && (
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    colSpan={10}
                    align="center"
                  >
                    <CircularProgress size={15} /> Carregando
                  </TableCell>
                </TableRow>
              )}

            {!isSorting &&
              !getProductResultsDetailedQuery.isLoading &&
              !getProductResultsDetailedQuery.isFetching &&
              sortedProducts?.map(
                (
                  product: SponsoredProductsResultsDetailedModelView[number]
                ) => {
                  return <ProductRow key={product.sku} product={product} />;
                }
              )}

            {!isSorting &&
              !getProductResultsDetailedQuery.isLoading &&
              !getProductResultsDetailedQuery.isFetching &&
              sortedProducts?.length === 0 && (
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    colSpan={10}
                    align="center"
                  >
                    Não possui produtos
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
    </StyledContainer>
  );
}
